import { Suspense, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { doLogout } from 'store/slices/auth';
import { setHeaderAppUrl } from 'store/slices/header';
import { selectIsUserDataLoading, selectUserActivePartnerId, setUserData } from 'store/slices/userData';
import { getPartnerSettingsSuccess, setPartnerCurrencies } from 'store/slices/partnerSettings';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useLocalization } from 'hooks/useLocalization';
import { useChatbotWidget } from 'hooks/useChatbotWidget';
// Import UI Components
import { Toaster, BusyLoader } from '@geneui/components';
// Import Components
import Header from 'components/Header';
import BMENewsModal from 'components/BMENewsModal';
// import MaintenanceModal from 'components/MaintenanceModal'; // Uncomment when need to use Maintenance Modal
import MainAppRoutes from './MainAppRoutes';
// Import Services
import { subscribe } from 'services/event';
import { UtilsHttpService } from 'services/http';
// Import Constants
import { AuthProvidersTypes, DefaultLangId, GlobalEvents, SubscriberSources } from 'constants/common';
import { BrowserStorageKeys } from 'constants/browserStorage';
// Import Styles
import 'assets/scss/defaults.scss';

const MainApp = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { url } = useRouteMatch();
    const [setLocalization] = useLocalization();

    const isUserDataLoading = useAppSelector(selectIsUserDataLoading);
    const userActivePartnerId = useAppSelector(selectUserActivePartnerId);

    const [isDataReady, setIsDataReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const isReady = isDataReady && !isLoading;

    useChatbotWidget(isReady);

    const { doGetRequest } = useRequest();
    const { getPartnerSettingsRequest, getUserInfoRequest, getPartnerCurrenciesRequest } = useCancelableRequests(
        {
            getPartnerSettingsRequest: UtilsHttpService.getPartnerSettings,
            getUserInfoRequest: UtilsHttpService.getUserInfo,
            getPartnerCurrenciesRequest: UtilsHttpService.getPartnerCurrencies,
        },
        { takeLatest: ['getPartnerSettingsRequest', 'getUserInfoRequest', 'getPartnerCurrenciesRequest'] },
    );

    const getUserInfoHandler = () => {
        if (!isUserDataLoading) return;
        doGetRequest(getUserInfoRequest.request, {
            successCallback: (data) => {
                const { Settings, ActivePartner, UserSession } = data;
                const Language = Settings.Language || DefaultLangId;
                const Timezone = ActivePartner.TimeZone;

                localStorage.setItem(BrowserStorageKeys.timezone, `${Timezone}`);

                if (UserSession?.ProviderId === AuthProvidersTypes.Password) {
                    localStorage.setItem(BrowserStorageKeys.isCRMSignIn, 'true');
                } else {
                    localStorage.removeItem(BrowserStorageKeys.isCRMSignIn);
                }

                dispatch(setUserData(data));
                setLocalization(Language, () => setIsDataReady(true));
                setIsLoading(false);
            },
            errorCallback: () => {
                // TODO: handle error
                localStorage.removeItem(BrowserStorageKeys.accessToken);
                dispatch(doLogout());
            },
        });
    };

    const getPartnerSettingsHandler = () => {
        if (!isLoading) {
            doGetRequest(getPartnerSettingsRequest.request, {
                successCallback: (data) => {
                    dispatch(getPartnerSettingsSuccess(data));
                },
            });
        }
    };

    const getPartnerCurrenciesHandler = () => {
        doGetRequest(getPartnerCurrenciesRequest.request, {
            successCallback: (data) => {
                dispatch(setPartnerCurrencies(data));
            },
        });
    };

    const init = () => {
        getPartnerCurrenciesHandler();
        dispatch(setHeaderAppUrl(url));
        subscribe(GlobalEvents.PartnerChanged, SubscriberSources.UserInfo, getUserInfoHandler);
        subscribe(GlobalEvents.PartnerChanged, SubscriberSources.PartnerCurrencies, getPartnerCurrenciesHandler);
        subscribe(GlobalEvents.UserSettingsChange, SubscriberSources.UserInfo, getUserInfoHandler);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getPartnerSettingsHandler, [isLoading, userActivePartnerId, t]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getUserInfoHandler, [isUserDataLoading]);

    return (
        <div>
            <div className="crm-wrapper">
                {!(isLoading || !isDataReady) && <Header setIsLoading={setIsLoading} isLoading={isLoading} />}
                <div className="crm-content">
                    {isLoading || !isDataReady ? (
                        <BusyLoader
                            isBusy={true}
                            type="spinner"
                            spinnerSize="big"
                            className="crm-full-width crm-justify-content-center"
                        />
                    ) : (
                        <Suspense
                            fallback={
                                <BusyLoader
                                    isBusy={true}
                                    type="spinner"
                                    spinnerSize="big"
                                    className="crm-full-width crm-justify-content-center"
                                />
                            }
                        >
                            <MainAppRoutes />
                        </Suspense>
                    )}
                </div>

                <Toaster notificationIcon={true} />
            </div>

            <BMENewsModal isReady={isReady} />

            {/* {isReady && <MaintenanceModal />} Uncomment to use Maintenance Modal */}
        </div>
    );
};

export default memo(MainApp);
